export interface User {
  id: number;
  name: string;
  role: 'admin' | 'adult' | 'teen' | 'child';
  preferences: Record<string, unknown>;
  budgetUsed?: number;
  budgetLimit?: number;
}

export interface Source {
  title: string;
  url: string;
  snippet: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  modeTransition?: string;
  persona?: PersonaId;
  timestamp?: number;
}

export interface Conversation {
  id: string;
  persona: PersonaId;
  title: string;
  messages: Message[];
  ventureContext?: VentureContext;
  planMode?: string;
  createdAt?: number;
  updatedAt?: number;
}

export interface VentureContext {
  action?: {
    type: string;
    label: string;
    preview: string;
  };
  sources?: Source[];
  budget?: number;
}

export interface PlanNavigation {
  current: number;
  next: number;
  previous: number;
  total: number;
  step: string;
}

export interface PlanState {
  mode: string;
  modeLabel: string;
  nextMode: string;
  navigation: PlanNavigation;
  canAdvance: boolean;
}

export type PersonaId = 'chat' | 'code' | 'research' | 'news' | 'plan' | 'venture';

export interface PersonaConfig {
  id: PersonaId;
  label: string;
  icon: string;
  endpoint: string;
  color: string;
}

export const PERSONAS: PersonaConfig[] = [
  { id: 'chat', label: 'Chat', icon: '💬', endpoint: '/api/chat', color: '#2563eb' },
  { id: 'code', label: 'Code', icon: '💻', endpoint: '/api/code', color: '#7c3aed' },
  { id: 'research', label: 'Research', icon: '🔬', endpoint: '/api/research', color: '#059669' },
  { id: 'news', label: 'News', icon: '📰', endpoint: '/api/news', color: '#dc2626' },
  { id: 'plan', label: 'Plan', icon: '📋', endpoint: '/api/plan', color: '#d97706' },
  { id: 'venture', label: 'Venture', icon: '🚀', endpoint: '/api/venture', color: '#ea580c' },
];

export interface LoginResponse {
  token: string;
  user: User;
}

export interface ChatResponse {
  response: string;
  conversationId: string;
  persona?: string;
  budget?: number;
  sources?: Source[];
  action?: {
    type: string;
    label: string;
    preview: string;
  };
  mode?: string;
  modeLabel?: string;
  nextMode?: string;
  navigation?: PlanNavigation;
  canAdvance?: boolean;
  newConversationId?: string;
  nextModes?: string[];
  error?: string;
}

export interface AdminStats {
  totalUsers: number;
  totalConversations: number;
  dailyUsage: { date: string; tokens: number; cost: number }[];
  topUsers: { name: string; usage: number }[];
  costBreakdown: { category: string; amount: number }[];
}

export interface AdminUser {
  id: number;
  name: string;
  role: 'admin' | 'adult' | 'teen' | 'child';
  pin?: string;
  budgetLimit: number;
  budgetUsed: number;
  enabled: boolean;
}

export interface BudgetSettings {
  globalPool: number;
  defaultRolePercentages: Record<string, number>;
}
